import { Transform } from 'class-transformer';
import { DateTruncEnum, GAEventPlatform, GAEventTypes, OrderStatusEnum } from '../../enums';
import { arrayOrStringToArray, jsonStringToObject } from '../../types-transformers';
import { IsNotEmpty, IsOptional, IsUUID } from 'class-validator';
import { PaginationFilter } from '../../interfaces/filters/pagination.filter';
import { DateFilter } from '../../interfaces/filters/date.filter';
import { SearchFilter } from '../../interfaces/filters/search.filter';

export class CommunityStatsFilter implements PaginationFilter, DateFilter, SearchFilter {
	@IsNotEmpty({ message: 'Community ID filter is required' })
	@IsUUID('4', { message: 'Community ID filter must be a valid UUID' })
	public communityId: string;

	@IsNotEmpty()
	public periodFrom: Date;

	@IsNotEmpty()
	public periodTo: Date;

	public page?: number;
	public pageSize?: number;

	public search?: string;

	public granularity?: DateTruncEnum = DateTruncEnum.Day;
}

export class EventsFilter {
	public baseId: string;
	public instance?: Date;
}

export class OrderStatsFilter {
	// entities
	public orderUserIds?: string[];
	public ticketUserIds?: string[];

	public forAllEvents?: boolean;
	public events?: EventsFilter[];

	public forAllMembershipTiers?: boolean;
	public membershipTierIds?: string[];

	public forAllChallenges?: boolean;
	public challengeIds?: string[];

	// statuses
	// use 'successful' status to get only successful orders
	// use 'refunded' status to get only refunded orders
	// or both
	public paymentStatuses?: OrderStatusEnum[];

	public excludeManuallyAdded?: boolean;
}

export class RevenueFilter extends CommunityStatsFilter implements OrderStatsFilter {
	public orderUserIds?: string[];

	public forAllEvents?: boolean;
	public events?: EventsFilter[];

	public forAllMembershipTiers?: boolean;
	public membershipTierIds?: string[];

	public forAllChallenges?: boolean;
	public challengeIds?: string[];

	public paymentStatuses?: OrderStatusEnum[];

	public excludeManuallyAdded?: boolean;
}

export class RsvpFilter extends CommunityStatsFilter implements OrderStatsFilter {
	public orderUserIds?: string[];
	public ticketUserIds?: string[];

	public forAllEvents?: boolean;
	public events?: EventsFilter[];

	public forAllMembershipTiers?: boolean;
	public membershipTierIds?: string[];

	public forAllChallenges?: boolean;
	public challengeIds?: string[];
}

export class TopEventsFilter extends CommunityStatsFilter {
	public topBy: 'revenue' | 'rsvps' = 'revenue';
}

export class RetentionFilter implements OrderStatsFilter, PaginationFilter, Pick<CommunityStatsFilter, 'communityId' | 'granularity' | 'page' | 'pageSize' | 'search'> {
	@IsOptional()
	type?: 'returning' | 'first-timers';

	@IsNotEmpty({ message: 'Community ID filter is required' })
	@IsUUID('4', { message: 'Community ID filter must be a valid UUID' })
	public communityId: string;

	public page?: number;
	public pageSize?: number;

	public search?: string;

	public granularity?: DateTruncEnum = DateTruncEnum.Day;

	public orderUserIds?: string[];
	public ticketUserIds?: string[];

	public forAllEvents?: boolean;
	public events?: EventsFilter[];

	public forAllMembershipTiers?: boolean;
	public membershipTierIds?: string[];

	public forAllChallenges?: boolean;
	public challengeIds?: string[];
}

export class MembershipFilter extends CommunityStatsFilter {
	@Transform(arrayOrStringToArray)
	public membershipIds?: string[];

	@Transform(arrayOrStringToArray)
	public membershipTierIds?: string[];
}

export class GAEventsFilter {
	public event_names?: GAEventTypes[];
	public platforms?: GAEventPlatform[];

	public baseEventId?: string;
	public eventInstance?: string;
	public authorId?: string;

	public referralCode?: string;

	public forAllEvents?: boolean;
	public events?: EventsFilter[];

	@IsNotEmpty({ message: 'Period from filter is required' })
	public periodFrom: string;
	public periodTo?: string;
}

export class CommunityGAStatsFilter implements Pick<CommunityStatsFilter, 'communityId' | 'periodFrom' | 'periodTo'> {
	@IsNotEmpty({ message: 'Community ID filter is required' })
	@IsUUID('4', { message: 'Community ID filter must be a valid UUID' })
	public communityId: string;

	@IsOptional()
	public periodFrom: Date;

	@IsOptional()
	public periodTo: Date;

	@IsOptional()
	@Transform(jsonStringToObject)
	public events?: EventsFilter[];
}
